import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Loader2, ChevronDown, X, Download, Languages, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { StatusBadge } from '@/components/admin/StatusBadge';
import { QrDownloadButton } from '@/components/admin/AdminShare';

const CATEGORIES = [
  { slug: 'gastronomia', label: 'Gastronomia' },
  { slug: 'eventos', label: 'Eventos y fiestas' },
  { slug: 'playas', label: 'Playas' },
  { slug: 'emergencias', label: 'Emergencias' },
  { slug: 'apps', label: 'Apps utiles' },
  { slug: 'sobre_nosotros', label: 'Sobre nosotros' },
];

const emptyForm = {
  category: 'gastronomia', title_es: '', title_en: '', title_de: '',
  summary_es: '', summary_en: '', summary_de: '',
  content_es: '', content_en: '', content_de: '',
  image: '', image_credit_es: '', image_credit_en: '', image_credit_de: '', phone: '', links: [], order: 0,
};

const AdminArticles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [filterCat, setFilterCat] = useState('');
  const [showDelete, setShowDelete] = useState(null);
  const [importing, setImporting] = useState(false);
  const [translatingId, setTranslatingId] = useState(null);

  useEffect(() => { loadArticles(); }, []);

  const loadArticles = async () => {
    try {
      const res = await api.getArticles({ show_hidden: true, status: 'all' });
      setArticles(res.data);
    } catch (e) {
      toast.error('Error al cargar articulos');
    } finally { setLoading(false); }
  };

  const openEditor = (article = null) => {
    if (article) {
      setEditingId(article.id);
      setFormData({
        category: article.category || 'gastronomia',
        title_es: article.title_es || '', title_en: article.title_en || '', title_de: article.title_de || '',
        summary_es: article.summary_es || '', summary_en: article.summary_en || '', summary_de: article.summary_de || '',
        content_es: article.content_es || '', content_en: article.content_en || '', content_de: article.content_de || '',
        image: article.image || '', phone: article.phone || '',
        image_credit_es: article.image_credit_es || '', image_credit_en: article.image_credit_en || '', image_credit_de: article.image_credit_de || '',
        links: article.links || [], order: article.order || 0,
      });
    } else {
      setEditingId(null);
      setFormData(emptyForm);
    }
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!formData.title_es) { toast.error('Titulo ES obligatorio'); return; }
    setSaving(true);
    try {
      const payload = { ...formData, phone: formData.phone || null };
      if (editingId) {
        await api.updateArticle(editingId, payload);
        toast.success('Articulo actualizado');
      } else {
        await api.createArticle(payload);
        toast.success('Articulo creado');
      }
      setShowEditor(false);
      loadArticles();
    } catch (e) {
      toast.error('Error al guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteArticle(id);
      toast.success('Articulo eliminado');
      setShowDelete(null);
      loadArticles();
    } catch (e) { toast.error('Error al eliminar'); }
  };

  const addLink = () => {
    setFormData(prev => ({ ...prev, links: [...prev.links, { name: '', url: '' }] }));
  };

  const updateLink = (idx, field, val) => {
    setFormData(prev => ({
      ...prev,
      links: prev.links.map((l, i) => i === idx ? { ...l, [field]: val } : l)
    }));
  };

  const removeLink = (idx) => {
    setFormData(prev => ({ ...prev, links: prev.links.filter((_, i) => i !== idx) }));
  };

  const filtered = filterCat ? articles.filter(a => a.category === filterCat) : articles;

  const handleImportGoodBarber = async () => {
    setImporting(true);
    try {
      const res = await api.importGoodBarberArticles();
      toast.success(res.data.message || 'Importacion completada');
      loadArticles();
    } catch (e) {
      toast.error('Error al importar: ' + (e.response?.data?.detail || e.message));
    } finally { setImporting(false); }
  };

  const handleToggleVisibility = async (article) => {
    const newHidden = !article.hidden;
    try {
      await api.updateArticle(article.id, { hidden: newHidden });
      setArticles(prev => prev.map(a => a.id === article.id ? { ...a, hidden: newHidden } : a));
      toast.success(newHidden ? 'Articulo oculto' : 'Articulo visible');
    } catch (e) {
      toast.error('Error al cambiar visibilidad');
    }
  };

  const handleTranslate = async (articleId) => {
    setTranslatingId(articleId);
    try {
      const res = await api.translateArticle(articleId);
      if (res.data.success) {
        toast.success(`Articulo traducido (${res.data.translated_fields.length} campos)`);
        loadArticles();
      } else {
        toast.warning(res.data.message || 'No se pudo traducir');
      }
    } catch (e) {
      toast.error('Error al traducir: ' + (e.response?.data?.detail || e.message));
    } finally { setTranslatingId(null); }
  };

  const handleTranslateAll = async () => {
    const untranslated = articles.filter(a => !a.title_en || !a.title_de);
    if (untranslated.length === 0) {
      toast.info('Todos los articulos ya estan traducidos');
      return;
    }
    if (!window.confirm(`¿Traducir ${untranslated.length} articulos sin traducir?`)) return;
    
    setTranslatingId('all');
    try {
      const res = await api.translateAllArticles();
      if (res.data.success) {
        toast.success(`Traducidos ${res.data.translated} articulos`);
        loadArticles();
      }
    } catch (e) {
      toast.error('Error al traducir: ' + (e.response?.data?.detail || e.message));
    } finally { setTranslatingId(null); }
  };

  // Count untranslated
  const untranslatedCount = articles.filter(a => !a.title_en || !a.title_de).length;

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin" size={24} /></div>;

  return (
    <div data-testid="admin-articles">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={filterCat}
            onChange={e => setFilterCat(e.target.value)}
            className="h-9 rounded-md border border-gray-300 bg-white px-3 text-sm"
            data-testid="filter-articles-cat"
          >
            <option value="">Todas ({articles.length})</option>
            {CATEGORIES.map(c => (
              <option key={c.slug} value={c.slug}>
                {c.label} ({articles.filter(a => a.category === c.slug).length})
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          {untranslatedCount > 0 && (
            <Button 
              onClick={handleTranslateAll} 
              disabled={translatingId === 'all'}
              className="bg-blue-500 hover:bg-blue-600 rounded-full text-sm" 
              data-testid="translate-all-articles-btn"
            >
              {translatingId === 'all' ? <Loader2 className="animate-spin mr-1" size={14} /> : <Languages size={14} className="mr-1" />}
              Traducir {untranslatedCount} sin traducir
            </Button>
          )}
          <Button onClick={handleImportGoodBarber} disabled={importing}
            className="bg-amber-500 hover:bg-amber-600 rounded-full text-sm" data-testid="import-goodbarber-btn">
            {importing ? <Loader2 className="animate-spin mr-1" size={14} /> : <Download size={14} className="mr-1" />}
            {importing ? 'Importando...' : 'Importar de Good Barber'}
          </Button>
          <Button onClick={() => openEditor()} className="bg-[#38B2AC] hover:bg-[#319795] rounded-full" data-testid="add-article-btn">
            <Plus size={16} className="mr-1" /> Articulo
          </Button>
        </div>
      </div>

      {/* List */}
      <div className="space-y-2">
        {filtered.map(article => (
          <div key={article.id} className={`flex items-center gap-3 bg-white rounded-xl p-3 border ${article.hidden ? 'border-gray-200 opacity-60' : 'border-gray-100'}`} data-testid={`article-row-${article.id}`}>
            {article.image && <img src={article.image} alt="" className="w-14 h-14 object-cover flex-shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm text-[#1D1D1F] truncate">{article.title_es}</p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400 capitalize">{article.category}</span>
                {article.hidden && <span className="text-xs text-red-400">Oculto</span>}
                <StatusBadge item={article} resource="articles" onChanged={loadArticles} />
                {(!article.title_en || !article.title_de) && (
                  <span className="text-xs text-amber-500">Sin traducir</span>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleToggleVisibility(article)}
              title={article.hidden ? 'Mostrar articulo' : 'Ocultar articulo'}
              data-testid={`toggle-visibility-${article.id}`}
            >
              {article.hidden ? <EyeOff size={16} className="text-gray-400" /> : <Eye size={16} className="text-green-500" />}
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => handleTranslate(article.id)} 
              disabled={translatingId === article.id}
              title="Traducir a EN/DE"
              data-testid={`translate-article-${article.id}`}
            >
              {translatingId === article.id ? <Loader2 size={16} className="animate-spin" /> : <Languages size={16} className="text-blue-500" />}
            </Button>
            <QrDownloadButton kind="a" itemId={article.id} name={article.title_es} />
            <Button variant="ghost" size="icon" onClick={() => openEditor(article)} data-testid={`edit-article-${article.id}`}>
              <Edit size={16} />
            </Button>
            <Button variant="ghost" size="icon" className="text-red-500" onClick={() => setShowDelete(article)} data-testid={`delete-article-${article.id}`}>
              <Trash2 size={16} />
            </Button>
          </div>
        ))}
      </div>

      {/* Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar articulo' : 'Nuevo articulo'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categoria *</Label>
                <select value={formData.category} onChange={e => setFormData(p => ({ ...p, category: e.target.value }))}
                  className="flex h-9 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm" data-testid="article-category">
                  {CATEGORIES.map(c => <option key={c.slug} value={c.slug}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <Label>Orden</Label>
                <Input type="number" value={formData.order} onChange={e => setFormData(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Titulo (ES) *</Label><Input value={formData.title_es} onChange={e => setFormData(p => ({ ...p, title_es: e.target.value }))} data-testid="article-title-es" /></div>
              <div><Label>Title (EN)</Label><Input value={formData.title_en} onChange={e => setFormData(p => ({ ...p, title_en: e.target.value }))} /></div>
              <div><Label>Titel (DE)</Label><Input value={formData.title_de} onChange={e => setFormData(p => ({ ...p, title_de: e.target.value }))} /></div>
            </div>
            <div><Label>Imagen URL</Label><Input value={formData.image} onChange={e => setFormData(p => ({ ...p, image: e.target.value }))} placeholder="https://..." /></div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="article-image-credit-fields">
              {['es', 'en', 'de'].map(l => (
                <div key={l}><Label>Crédito portada ({l.toUpperCase()})</Label><Input value={formData[`image_credit_${l}`]} onChange={e => setFormData(p => ({ ...p, [`image_credit_${l}`]: e.target.value }))} placeholder="Foto: Autor · CC BY 4.0" data-testid={`article-image-credit-${l}`} /></div>
              ))}
            </div>
            <div><Label>Telefono (opcional)</Label><Input value={formData.phone} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} placeholder="112" /></div>
            <div><Label>Resumen (ES)</Label><Textarea rows={2} value={formData.summary_es} onChange={e => setFormData(p => ({ ...p, summary_es: e.target.value }))} /></div>
            <div><Label>Contenido (ES) *</Label><Textarea rows={6} value={formData.content_es} onChange={e => setFormData(p => ({ ...p, content_es: e.target.value }))} data-testid="article-content-es" /></div>
            <div><Label>Summary (EN)</Label><Textarea rows={2} value={formData.summary_en} onChange={e => setFormData(p => ({ ...p, summary_en: e.target.value }))} /></div>
            <div><Label>Content (EN)</Label><Textarea rows={4} value={formData.content_en} onChange={e => setFormData(p => ({ ...p, content_en: e.target.value }))} /></div>

            {/* Links */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Enlaces</Label>
                <Button variant="outline" size="sm" onClick={addLink}><Plus size={14} className="mr-1" /> Enlace</Button>
              </div>
              {formData.links.map((link, idx) => (
                <div key={idx} className="flex gap-2 mb-2">
                  <Input placeholder="Nombre" value={link.name} onChange={e => updateLink(idx, 'name', e.target.value)} />
                  <Input placeholder="https://..." value={link.url} onChange={e => updateLink(idx, 'url', e.target.value)} />
                  <Button variant="ghost" size="icon" onClick={() => removeLink(idx)}><X size={14} /></Button>
                </div>
              ))}
            </div>

            <div className="flex gap-3 pt-2">
              <Button onClick={handleSave} disabled={saving} className="flex-1 bg-[#38B2AC] hover:bg-[#319795]" data-testid="save-article-btn">
                {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                {editingId ? 'Guardar cambios' : 'Crear articulo'}
              </Button>
              <Button variant="outline" onClick={() => setShowEditor(false)} className="flex-1">Cancelar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!showDelete} onOpenChange={() => setShowDelete(null)}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader><DialogTitle>Eliminar articulo</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">¿Seguro que quieres eliminar "{showDelete?.title_es}"?</p>
          <div className="flex gap-3 mt-4">
            <Button variant="destructive" onClick={() => handleDelete(showDelete.id)} className="flex-1" data-testid="confirm-delete-article">Eliminar</Button>
            <Button variant="outline" onClick={() => setShowDelete(null)} className="flex-1">Cancelar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminArticles;
